import { logger } from "../utils/logger";
import { apiClient } from "./axiosInstance";

/**
 * Setup Axios interceptors to handle authentication
 * - Request interceptor: Proactively adds fresh token to every request
 * - Response interceptor: Handles 401 errors by forcing token refresh
 * @param {Function} getAccessTokenSilently - Auth0 function to get a fresh token
 */
export function setupAxiosAuthInterceptor(getAccessTokenSilently) {
  // Remove any existing interceptors to prevent duplicates
  if (setupAxiosAuthInterceptor.requestId !== undefined) {
    apiClient.interceptors.request.eject(setupAxiosAuthInterceptor.requestId);
  }
  if (setupAxiosAuthInterceptor.responseId !== undefined) {
    apiClient.interceptors.response.eject(setupAxiosAuthInterceptor.responseId);
  }

  // REQUEST INTERCEPTOR: Add token to every outgoing request
  const requestId = apiClient.interceptors.request.use(
    async (config) => {
      try {
        // Auth0 handles caching and will return a valid token from cache if available
        const token = await getAccessTokenSilently();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // If no token is available, reject the request to prevent 403 errors
          logger.warn("No access token available, blocking request");
          return Promise.reject(
            new Error("Authentication required - no token available")
          );
        }
      } catch (error) {
        logger.error("Failed to get access token:", error);
        // Reject the request if we can't get a token
        return Promise.reject(error);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // RESPONSE INTERCEPTOR: Handle 401 errors by forcing token refresh
  const responseId = apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if the error is 401 and we haven't already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Force Auth0 to get a fresh token (bypass cache)
          const token = await getAccessTokenSilently({
            cacheMode: "off",
          });

          if (!token) {
            throw new Error("No token received from Auth0");
          }

          // Update the failed request's authorization header
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Retry the original request with the new token
          return apiClient(originalRequest);
        } catch (refreshError) {
          // If refresh fails, let error boundaries handle it
          logger.error("Token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  // Store the interceptor IDs so we can remove them later if needed
  setupAxiosAuthInterceptor.requestId = requestId;
  setupAxiosAuthInterceptor.responseId = responseId;

  return { requestId, responseId };
}
